import { useState, useEffect, ReactNode } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useForm, Controller } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { trpc } from '../../../../../../utils/trpc';
import { zodResolver } from '@hookform/resolvers/zod';
import { getQueryKey } from '@trpc/react-query';
import { useQueryClient } from '@tanstack/react-query';
import type { inferProcedureOutput } from '@trpc/server';
import * as z from 'zod';
import type { AppRouter } from '../../../../../../server/trpc/router';
import MuiPhoneNumber from 'material-ui-phone-number-2';
import { alpha, styled } from '@mui/material/styles';
import { window_open } from 'src/utils/flutter';
import { Alert, Snackbar } from '@mui/material';
import { useApp } from 'src/@carketa/app/AppContext';
import { OrganizationSettings } from 'src/types/organizations/organizations';
import { useAnalytics } from 'use-analytics';

const schema = z.object({
  first_name: z.string().min(2, 'First Name must be at least 2 characters'),
  last_name: z.string().min(2, 'Last Name must be at least 2 characters'),
  email: z.string().email().min(2, 'Email must be at least 2 characters'),
  phone: z.string().min(10, 'Phone must be at least 10 characters'),
  comments: z.string(),
});

const LinkStyled = styled('a')(({ theme }) => ({
  fontSize: 12,
  textDecoration: 'none',
  cursor: 'pointer',
  color: theme.palette.primary.main,
  '&:hover': {
    background: alpha(theme.palette.common.white, 0.25),
  },
}));

export type Vehicle = inferProcedureOutput<AppRouter['vehicles']['getById']>;
export type VehicleStickerOrId = inferProcedureOutput<
  AppRouter['vehicles']['getByStickerOrId']
>;

type Message = {
  open: boolean;
  message: string;
  type: 'success' | 'error';
};

interface Props {
  vehicle?: Vehicle | VehicleStickerOrId | undefined | null;
  open: boolean;
  handleClose: () => void;
}

export const CheckAvailabilityDialog: React.FC<Props> = ({
  vehicle,
  open = false,
  handleClose,
}) => {
  const { track } = useAnalytics();
  const { me } = useApp();
  const [vehicleId, setVehicleId] = useState<string | undefined | null>('');

  const [openMessage, setOpenMessage] = useState<Message>({
    open: false,
    message: '',
    type: 'success',
  });

  useEffect(() => {
    setVehicleId(vehicle?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const queryClient = useQueryClient();
  const key = getQueryKey(
    trpc.vehicles.getById,
    { vehicle_id: vehicleId || '' },
    'query'
  );

  const createLeadMutation = trpc.leads.createVehicleLead.useMutation({
    onError(error) {
      setOpenMessage({
        open: true,
        message: error.message,
        type: 'error',
      });
      console.error(error);
    },
    onSuccess() {
      setOpenMessage({
        open: true,
        message: 'Details successfully saved!',
        type: 'success',
      });

      track(
        'contact-dealer-submit',
        {
          ...getValues(),
          vehicle_id: vehicleId || '',
          salesAndMrktngEmails: (
            me?.organization?.settings as OrganizationSettings
          ).emailAddresses,
        },
        {
          component: 'sales-packet',
        }
      );

      sendLeadtoDealership.mutate({
        ...getValues(),
        vehicle_id: vehicleId || '',
        salesAndMrktngEmails: (
          me?.organization?.settings as OrganizationSettings
        ).emailAddresses,
      });

      console.log('success');
      queryClient.invalidateQueries(key);
      reset();

      handleClose();
    },
  });

  const sendLeadtoDealership = trpc.leads.sendLeadADFEmail.useMutation({
    onError(error) {
      console.error(error);
    },
    onSuccess() {
      console.log('success');
    },
  });

  const defaultValues = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    comments: '',
  };

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const onSubmit = (data: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    comments: string;
  }) => {
    const payload = {
      vehicle_id: vehicle?.id || '',
      ...data,
      lead_source: 'sales_packet',
      salesperson: '',
      notify: false,
    };

    createLeadMutation.mutate(payload);
  };

  const onHyperlinkClick = (url: string) => {
    window_open(url, '_blank', 'noreferrer');
  };

  const handleCloseMessage = () => {
    setOpenMessage({
      open: false,
      message: '',
      type: 'success',
    });
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Check Availability</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12, mb: 6 }}>
            Request information for this vehicle. Fill out this form and this
            dealership’s team will reach out to you.
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 6 }}>
              <FormControl fullWidth>
                <Controller
                  name="first_name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      label="First Name"
                      onChange={onChange}
                      placeholder="John"
                      error={Boolean(errors.first_name)}
                    />
                  )}
                />
                {errors?.first_name && (
                  <FormHelperText sx={{ color: 'error.main' }}>
                    {(errors.first_name.message as ReactNode) || ''}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 6 }}>
              <FormControl fullWidth>
                <Controller
                  name="last_name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      label="Last Name"
                      onChange={onChange}
                      placeholder="Doe"
                      error={Boolean(errors.last_name)}
                    />
                  )}
                />
                {errors?.last_name && (
                  <FormHelperText sx={{ color: 'error.main' }}>
                    {(errors.last_name.message as ReactNode) || ''}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 6, gap: 4 }}>
              <FormControl fullWidth>
                <Controller
                  name="email"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      type="email"
                      value={value}
                      label="Email"
                      onChange={onChange}
                      placeholder="johndoe@email.com"
                      error={Boolean(errors.email)}
                    />
                  )}
                />
                {errors?.email && (
                  <FormHelperText sx={{ color: 'error.main' }}>
                    {(errors.email.message as ReactNode) || ''}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 6, gap: 4 }}>
              <FormControl fullWidth>
                <Controller
                  name="phone"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <MuiPhoneNumber
                      name="phone"
                      label="Phone"
                      value={value}
                      onChange={onChange}
                      onlyCountries={['us']}
                      defaultCountry="us"
                      variant="outlined"
                      disableDropdown
                      disableCountryCode
                      error={Boolean(errors.phone)}
                    />
                  )}
                />
                {errors?.phone && (
                  <FormHelperText sx={{ color: 'error.main' }}>
                    {(errors.phone.message as ReactNode) || ''}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 6, gap: 4 }}>
              <FormControl fullWidth>
                <Controller
                  name="comments"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      multiline
                      rows={4}
                      value={value}
                      label="Comments(Optional)"
                      onChange={onChange}
                      placeholder="Comments"
                      error={Boolean(errors.comments)}
                    />
                  )}
                />
                {errors?.comments && (
                  <FormHelperText sx={{ color: 'error.main' }}>
                    {(errors.comments.message as ReactNode) || ''}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 6 }}>
              <Button
                size="large"
                variant="outlined"
                color="secondary"
                onClick={() => {
                  track(
                    'check-availability-cancel-click',
                    {},
                    {
                      component: 'sales-packet',
                    }
                  );
                  handleClose();
                  reset();
                }}
                sx={{ mr: 3 }}
              >
                Cancel
              </Button>
              <Button size="large" type="submit" variant="contained">
                Contact Dealer
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 6 }}>
              <Typography sx={{ fontSize: 12, fontStyle: 'italic' }}>
                By submitting my contact information through Carketa, I agree to
                receive communications from Carketa, from the vehicle’s seller,
                and from the seller’s agent(s). If I include my phone number, I
                agree to receive calls and text messages (including via
                automation). I can opt out at any time. I also agree to the{' '}
                <LinkStyled
                  onClick={() =>
                    onHyperlinkClick(
                      'https://carketa.com/terms-and-conditions/'
                    )
                  }
                >
                  Terms & Conditions
                </LinkStyled>{' '}
                and{' '}
                <LinkStyled
                  onClick={() =>
                    onHyperlinkClick('https://carketa.com/privacy-policy/')
                  }
                >
                  Privacy Policy
                </LinkStyled>
                , which explains how my data is used to better understand my
                vehicle shopping interests. This consent is not required as a
                condition of purchase. Standard message and data rates may
                apply.
              </Typography>
            </Box>
          </form>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={openMessage.open}
        onClose={handleCloseMessage}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert variant="filled" elevation={3} severity={openMessage.type}>
          {openMessage.message}
        </Alert>
      </Snackbar>
    </>
  );
};
